import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Send, Languages, Loader2 } from "lucide-react";
import ToothAssistantIcon from "./ToothAssistantIcon";
import { usePublicSettings } from "@/hooks/usePublicSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const ASSISTANT_NAME = "Dentini";

// Auto-detekto gjuhen nga ?host= (kur thirret nga embed.js ne nje faqe te jashtme)
// ose nga window.location.hostname (kur shfaqet direkt te /portal).
function detectLangFromHost() {
  if (typeof window === "undefined") return "sq";
  let host = "";
  try {
    const params = new URLSearchParams(window.location.search);
    host = (params.get("host") || window.location.hostname || "").toLowerCase();
  } catch (e) {
    host = (window.location.hostname || "").toLowerCase();
  }
  if (host.endsWith(".al")) return "sq";
  if (host.endsWith(".it")) return "it";
  if (host.endsWith(".com") || host.endsWith(".net") || host.endsWith(".org") || host.endsWith(".co.uk")) return "en";
  return "sq";
}

const LANG_OPTS = [
  { code: "sq", label: "Shqip", flag: "🇦🇱" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
];

const UI_LABELS = {
  sq: {
    title: `${ASSISTANT_NAME} · Asistentja Virtuale`,
    short: ASSISTANT_NAME,
    sub: "Konsultë paraprake & rezervime",
    placeholder: "Shkruani mesazhin tuaj…",
    poweredBy: (brand) => `${ASSISTANT_NAME} · Asistentja e ${brand}`,
    starting: "Po nis bisedën…",
    thinking: `${ASSISTANT_NAME} po shkruan…`,
    chooseLang: "Zgjidh gjuhën",
    error: "Pati një gabim. Provoni përsëri.",
  },
  en: {
    title: `${ASSISTANT_NAME} · Virtual Assistant`,
    short: ASSISTANT_NAME,
    sub: "Pre-consult & bookings",
    placeholder: "Type your message…",
    poweredBy: (brand) => `${ASSISTANT_NAME} · ${brand} Assistant`,
    starting: "Starting chat…",
    thinking: `${ASSISTANT_NAME} is typing…`,
    chooseLang: "Choose language",
    error: "Something went wrong. Try again.",
  },
  it: {
    title: `${ASSISTANT_NAME} · Assistente Virtuale`,
    short: ASSISTANT_NAME,
    sub: "Pre-consulto & prenotazioni",
    placeholder: "Scrivi il tuo messaggio…",
    poweredBy: (brand) => `${ASSISTANT_NAME} · Assistente ${brand}`,
    starting: "Avvio chat…",
    thinking: `${ASSISTANT_NAME} sta scrivendo…`,
    chooseLang: "Scegli lingua",
    error: "Qualcosa è andato storto. Riprova.",
  },
};

export default function AIChatWidget({ source = "portal", defaultOpen = false }) {
  const branding = usePublicSettings();
  const brandName = branding?.clinic_name || "DentArt";
  const [open, setOpen] = useState(defaultOpen);
  const [lang, setLang] = useState(() => detectLangFromHost());
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text:string, ts}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [bookingDone, setBookingDone] = useState(null);
  const scrollerRef = useRef(null);
  const labels = UI_LABELS[lang] || UI_LABELS.sq;

  // Greeting i menjehershem (shfaqet para se te vije pergjigja AI)
  const STATIC_GREETING = {
    sq: "Përshëndetje! 👋 Si mund t'ju ndihmoj? Mund të më pyesni për shërbimet tona ose të caktoni një takim.",
    en: "Hello! 👋 How can I help? Ask me about our services or book an appointment.",
    it: "Salve! 👋 Come posso aiutarti? Chiedimi dei nostri servizi o prenota un appuntamento.",
  };

  const startSession = async (langCode) => {
    setStarting(true);
    // Shfaq greeting statik menjehere (pa pritje)
    setMessages([{ role: "assistant", text: STATIC_GREETING[langCode] || STATIC_GREETING.sq, ts: new Date().toISOString() }]);
    setBookingDone(null);
    try {
      const { data } = await axios.post(`${API}/chat/public/start`, {
        language: langCode,
        source,
      });
      setSessionId(data.session_id);
      setLang(data.language);
      // Zevendeso greeting-un statik me te dhenat e backend-it (nese sjell te ndryshme)
      if (data.welcome && data.welcome.trim()) {
        setMessages([{ role: "assistant", text: data.welcome, ts: new Date().toISOString() }]);
      }
    } catch (e) {
      // Mbaj greeting-un statik nese backend deshton; mos shto error message
    } finally {
      setStarting(false);
    }
  };

  // start on first open (deferred to microtask to avoid sync setState in effect)
  useEffect(() => {
    if (!open || sessionId) return;
    const t = setTimeout(() => startSession(lang), 0);
    return () => clearTimeout(t);
  }, [open, sessionId, lang]);

  // Komunikim me embed.js — njofto host-in per open/close per backdrop-in
  useEffect(() => {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ source: "dentart-chat", event: open ? "open" : "close" }, "*");
      }
    } catch (e) {}
  }, [open]);

  // Dëgjo mbyllje nga host-i (kur perdoruesi klikton backdrop-in)
  useEffect(() => {
    const onMsg = (e) => {
      if (e && e.data && e.data.source === "dentart-host" && e.data.event === "close") {
        setOpen(false);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, loading]);

  const switchLang = async (code) => {
    if (code === lang) return;
    setLang(code);
    if (sessionId) {
      // Restart session in new language for a clean welcome
      await startSession(code);
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || loading || !sessionId) return;
    const userText = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: userText, ts: new Date().toISOString() }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat/public/${sessionId}/message`, { text: userText });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, ts: new Date().toISOString() }]);
      if (data.booking?.ok) {
        setBookingDone(data.booking);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: labels.error, ts: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-chat-launcher"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 rounded-full bg-[#2C4C3B] text-[#FDFBF7] pl-3 pr-5 py-2.5 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5 group"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#FDFBF7] text-[#2C4C3B] group-hover:scale-105 transition-transform">
            <ToothAssistantIcon size={22} pulse />
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[11px] uppercase tracking-wider opacity-80">Pyet</span>
            <span className="text-sm font-semibold">{labels.short}</span>
          </span>
        </button>
      )}

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed z-50 flex flex-col overflow-hidden ring-1 ring-black/5
                     bottom-0 right-0 sm:bottom-5 sm:right-5
                     w-full sm:w-[340px] sm:max-w-[calc(100vw-2rem)]
                     h-[100dvh] sm:h-[520px] sm:max-h-[calc(100vh-3rem)]
                     rounded-none sm:rounded-2xl"
          style={{
            background: "#FDFBF7",
            boxShadow:
              "0 30px 80px -20px rgba(44, 76, 59, 0.45), 0 12px 28px -8px rgba(44, 76, 59, 0.25), 0 0 0 1px rgba(44, 76, 59, 0.08)",
          }}
        >
          {/* Header */}
          <div className="bg-[#2C4C3B] text-[#FDFBF7] px-3 py-2.5 flex items-center justify-between">
            <div className="min-w-0 flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#FDFBF7] text-[#2C4C3B] shrink-0">
                <ToothAssistantIcon size={22} pulse />
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-[13px] truncate leading-tight">{labels.title}</div>
                <div className="text-[11px] opacity-80 truncate leading-tight mt-0.5">{labels.sub}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <div className="relative group">
                <button
                  className="p-1.5 rounded-md hover:bg-white/10"
                  data-testid="ai-chat-lang-button"
                  title={labels.chooseLang}
                >
                  <Languages size={16} />
                </button>
                <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-white text-[#1A1A1A] rounded-lg shadow-lg border border-[#E2DFD8] py-1 min-w-[120px] z-10">
                  {LANG_OPTS.map((o) => (
                    <button
                      key={o.code}
                      onClick={() => switchLang(o.code)}
                      data-testid={`ai-chat-lang-${o.code}`}
                      className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-[#EAE4D9] ${
                        lang === o.code ? "bg-[#EAE4D9] font-medium" : ""
                      }`}
                    >
                      <span>{o.flag}</span> {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md hover:bg-white/10"
                data-testid="ai-chat-close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto px-2.5 py-2.5 space-y-2"
            style={{
              background:
                "linear-gradient(180deg, #FDFBF7 0%, #F5EFE6 100%)",
              backgroundImage:
                "linear-gradient(180deg, #FDFBF7 0%, #F5EFE6 100%), radial-gradient(circle at 20% 0%, rgba(44, 76, 59, 0.04) 0%, transparent 50%)",
            }}
          >
            {starting && (
              <div className="text-center text-xs text-[#5C5C5C] py-4 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> {labels.starting}
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.text} />
            ))}
            {loading && (
              <Bubble role="assistant" text={`⋯ ${labels.thinking}`} pulse />
            )}
            {bookingDone && (
              <div className="rounded-lg bg-[#2C4C3B]/10 border border-[#2C4C3B]/30 p-3 text-xs">
                <div className="font-semibold text-[#2C4C3B]">✓ Reference: {bookingDone.appointment_id?.slice(0, 8)}</div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-[#E2DFD8] p-2.5 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={labels.placeholder}
              data-testid="ai-chat-input"
              disabled={!sessionId || loading || starting}
              className="flex-1 px-3 py-2 text-sm rounded-md border border-[#E2DFD8] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading || starting}
              data-testid="ai-chat-send"
              className="rounded-md bg-[#2C4C3B] text-[#FDFBF7] px-3 py-2 hover:bg-[#1F3A2B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Send size={14} />
            </button>
          </form>
          <div className="text-center text-[10px] text-[#5C5C5C] py-1 border-t border-[#E2DFD8] bg-[#FDFBF7]">
            {typeof labels.poweredBy === "function" ? labels.poweredBy(brandName) : labels.poweredBy}
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ role, text, pulse, testId }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={testId || `ai-chat-bubble-${role}`}
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] whitespace-pre-wrap leading-snug ${
          isUser
            ? "bg-[#2C4C3B] text-[#FDFBF7] rounded-br-sm shadow-md"
            : `bg-white border border-[#E2DFD8] text-[#1A1A1A] rounded-bl-sm shadow-sm ${pulse ? "animate-pulse" : ""}`
        }`}
        style={isUser ? { boxShadow: "0 4px 12px -2px rgba(44, 76, 59, 0.25)" } : { boxShadow: "0 2px 8px -2px rgba(44, 76, 59, 0.08)" }}
      >
        {text}
      </div>
    </div>
  );
}
